// Heterogeneous editor for a story's body — a sequence of paragraphs
// and inline images. Built to look and feel like ArrayEditor but with
// two "add" buttons (paragraph or image) at the foot.

import { Trash2, ArrowUp, ArrowDown, Image as ImageIcon, Type } from 'lucide-react';
import RichTextArea from './RichTextArea';
import { ImagePicker } from './ImagePicker';
import { uid } from './blocks';
import type { StoryBodyItem } from '@shared/types';

export function BodyEditor({
  items,
  onChange,
  draftId,
}: {
  items: StoryBodyItem[];
  onChange: (next: StoryBodyItem[]) => void;
  draftId: string;
}) {
  const update = (idx: number, next: StoryBodyItem) => {
    const arr = [...items];
    arr[idx] = next;
    onChange(arr);
  };
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const arr = [...items];
    [arr[from], arr[to]] = [arr[to], arr[from]];
    onChange(arr);
  };

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={item.id} className="border border-slate-200 rounded-md p-3 bg-slate-50 relative group">
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            {idx > 0 && (
              <button onClick={() => move(idx, idx - 1)} className="p-1 hover:bg-white rounded text-slate-500 hover:text-slate-700">
                <ArrowUp size={12} />
              </button>
            )}
            {idx < items.length - 1 && (
              <button onClick={() => move(idx, idx + 1)} className="p-1 hover:bg-white rounded text-slate-500 hover:text-slate-700">
                <ArrowDown size={12} />
              </button>
            )}
            <button onClick={() => remove(idx)} className="p-1 hover:bg-white rounded text-slate-500 hover:text-red-600">
              <Trash2 size={12} />
            </button>
          </div>

          {item.type === 'paragraph' ? (
            <>
              <input
                className="w-full mb-2 px-2 py-1.5 text-xs border border-slate-200 rounded bg-white font-semibold"
                value={item.lead || ''}
                onChange={(e) => update(idx, { ...item, lead: e.target.value })}
                placeholder="Bold lead-in (optional, e.g. 'The context:')"
              />
              <RichTextArea
                value={item.body || ''}
                onChange={(v) => update(idx, { ...item, body: v })}
                rows={3}
                placeholder="Paragraph body"
              />
            </>
          ) : (
            <ImagePicker
              value={{ url: item.url, alt: item.alt, caption: item.caption }}
              onChange={(v) => update(idx, { ...item, url: v.url, alt: v.alt, caption: v.caption })}
              draftId={draftId}
            />
          )}
        </div>
      ))}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange([...items, { id: uid(), type: 'paragraph', lead: '', body: '' }])}
          className="flex-1 py-2 text-xs font-semibold text-blue-700 border border-dashed border-blue-300 rounded-md hover:bg-blue-50 flex items-center justify-center gap-1.5"
        >
          <Type size={12} /> Add paragraph
        </button>
        <button
          type="button"
          onClick={() => onChange([...items, { id: uid(), type: 'image', url: '', alt: '', caption: '' }])}
          className="flex-1 py-2 text-xs font-semibold text-blue-700 border border-dashed border-blue-300 rounded-md hover:bg-blue-50 flex items-center justify-center gap-1.5"
        >
          <ImageIcon size={12} /> Add image
        </button>
      </div>
    </div>
  );
}
